import * as React from 'react';
import { graphql } from 'gatsby';
import './styles.scss';
import {MarkdownRemark} from '../models/markdown-remark';
import {TitleSection} from '../components/change-impact/title-section';
import {BackgroundFigure} from '../components/shared/background-figure';
import {BasePageData, BasePageFooter, BasePageHeader} from '../helpers/base-page';
import {RegularSectionModel} from '../models/regular-section-model';
import {RegularSection} from '../components/shared/regular-section';

interface ChangeImpactPageData extends BasePageData {
  changeImpact: MarkdownRemark<{
    seoTitle: string;
    title: string;
    heroImage: string;
    altText: string;
    subTitle: string;
    subList: Array<{listItem: string;}>
    subSections: Array<RegularSectionModel>;
  }>;
}

const ChangeImpactPage = ({data}: {data: ChangeImpactPageData}): JSX.Element => {

  return (
    <React.Fragment>
      <BasePageHeader
        siteTitle={data.changeImpact.edges[0].node.frontmatter.seoTitle ?? data.metaInfo.edges[0].node.frontmatter.siteTitle}
        siteDescription={data.metaInfo.edges[0].node.frontmatter.siteDescription}
        siteDescriptionTitle={data.metaInfo.edges[0].node.frontmatter.siteDescriptionTitle}
        siteImage={data.metaInfo.edges[0].node.frontmatter.siteImage}
        siteUrl={data.metaInfo.edges[0].node.frontmatter.siteUrl}
        logoLight={data.logos.edges[0].node.frontmatter.logoHeaderLight}
        logoDark={data.logos.edges[0].node.frontmatter.logoHeaderDark}
        logoShort={data.logos.edges[0].node.frontmatter.logoShort}
        links={data.navigation.edges[0].node.frontmatter.topCenterNavigation}
        modules={data.modules.edges.map(m => m.node.frontmatter)}
        usecases={data.usecases.edges.map(u => u.node.frontmatter)}
        hubspotFormId={data.hubspot.edges[0].node.frontmatter.hubSpotFormId}
        hubspotPortalId={data.hubspot.edges[0].node.frontmatter.hubSpotPortalId}
        contactFormConfig={data.contactFormConfig.edges[0].node.frontmatter}
      />
      <TitleSection
        title={data.changeImpact.edges[0].node.frontmatter.title}
        text={data.changeImpact.edges[0].node.frontmatter.subTitle}
        image={data.changeImpact.edges[0].node.frontmatter.heroImage}
        alt={data.changeImpact.edges[0].node.frontmatter.altText}
        list={data.changeImpact.edges[0].node.frontmatter.subList.map(x => x.listItem)}
      />
      <div className={'desktop-only'}>
        <BackgroundFigure
          type={'strips'}
          color={'#ffc02a'}
          size={{width: 76, height: 203}}
          position={{left: 0, top: 1210}}
        />
        <BackgroundFigure
          type={'circle'}
          color={'#ffc02a'}
          size={{width: 23, height: 46}}
          position={{right: 0, top: 810}}
          style={{
            zIndex: 0,
            borderRadius: 23,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0
          }}
        />
        <div style={{
          position: 'absolute',
          left: 0,
          top: 1420,
          width: 86,
          height: 155,
          overflow: 'hidden'
        }}>
          <BackgroundFigure
            type={'triangle'}
            color={'#dee4fb'}
            size={{width: 60, height: 60}}
            position={{right: 30, bottom: 37}}
            style={{
              transform: `
                rotate(51deg)
                skewX(-30deg)
                scale(1,.866)
              `
            }}
          />
        </div>
      </div>
      {
        data.changeImpact.edges[0].node.frontmatter.subSections.map((section, index, arr) => (
          <RegularSection {...section.section} key={index} style={{
            ...(index === arr.length - 1 && {borderBottom: 'none'})
          }} />
        ))
      }
      <BasePageFooter
        feedbackTitle={data.contact.edges[0].node.frontmatter.title}
        feedbackLabel={data.contact.edges[0].node.frontmatter.subText}
        feedbackButtonText={data.contact.edges[0].node.frontmatter.buttonText}
        feedbackInputPlaceholder={data.contact.edges[0].node.frontmatter.inputPlaceholder}
        navigation={data.navigation}
        integrations={data.integrations}
        blogArticles={data.blogArticles}
        blogCategories={data.blogCategories}
        features={data.features.edges.map(f => f.node.frontmatter)}
        footerLogo={data.logos.edges[0].node.frontmatter.logoFooter}
      />
    </React.Fragment>
  );
};

export default ChangeImpactPage;

export const pageQuery = graphql`
  {
    ...BaseData
    changeImpact: allMarkdownRemark(filter: {
      fileAbsolutePath: {
        regex: "/changeImpact.md$/"
      }
    }) {
      edges {
        node {
          frontmatter {
            seoTitle
            title
            heroImage
            altText
            subTitle
            subList {
              listItem
            }
            subSections {
              section {
                alignment
                label
                color
                background
                title
                image
                altText
                subText
              }
            }
          }
        }
      }
    }
  }
`;
